/* import __COLOCATED_TEMPLATE__ from './list-item.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import {
  humanReadableObjectNames,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { tracked } from '@glimmer/tracking';
import type SuggestionService from 'embercom/services/suggestion-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type RouterService from '@ember/routing/router-service';
import type { OptimizeSuggestionItem } from 'embercom/services/optimize-suggestions-service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import type { EntityType } from 'embercom/models/data/entity-types';

interface Signature {
  Args: {
    contentWrapper: ContentWrapper | TaskSuggestion;
    activeContentId?: string;
    activeContentType?: string;
    suggestion: OptimizeSuggestionItem;
    borderOnHover?: boolean;
    showTopics?: boolean;
    showTitle?: boolean;
    loadSuggestions?: () => void;
  };
}

export default class ListItem extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare store: $TSFixMe;
  @service declare appService: AppService;
  @service declare suggestionService: SuggestionService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare router: RouterService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @tracked isHovering = false;
  @tracked loadedDuplicateReason: string | null = null;
  @tracked loadedContradictingReason: string | null = null;
  @tracked processedReviewReason: string | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
  }

  get content() {
    if (this.args.suggestion.suggestionType === 'content-gap') {
      let contentWrapper = this.args.contentWrapper as ContentWrapper;
      return contentWrapper.contents.firstObject;
    }
    return null;
  }

  get ranking() {
    return this.args.suggestion.ranking;
  }

  get isActive() {
    if (!this.args.activeContentId) {
      return false;
    }
    return this.args.activeContentId === this.contentId;
  }

  get contentId() {
    return this.args.suggestion.entityId;
  }

  get contentType() {
    return this.content?.contentType || '';
  }

  get contentObjectName() {
    return objectNames[this.contentType as EntityType];
  }

  get numberOfPendingSuggestions() {
    return this.content?.contentData.number_of_pending_review_requests;
  }

  get reviewReason() {
    if (this.args.suggestion.description) {
      return this.args.suggestion.description;
    }
    return this.intl.t('ai-agent.suggestions.list-item.review-reason-fallback');
  }

  get topic(): string {
    return this.args.suggestion.topicList[0];
  }

  get subtopics(): string[] {
    return this.args.suggestion.subtopicList;
  }

  get mostRecentContentReviewRequestCreatedAt() {
    return this.args.suggestion.createdAt;
  }

  get leastRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.least_recent_content_review_request_created_at;
  }

  get suggestedAt() {
    return this.args.suggestion.createdAt;
  }

  get title() {
    return this.args.suggestion.title;
  }

  get conversationsCount() {
    return this.args.suggestion.conversationIds.length;
  }

  get editorMode(): EditorMode.REVIEW {
    return EditorMode.REVIEW;
  }

  get isCompleted() {
    return this.contentSuggestionsService.isSuggestionCompleted({
      entityId: parseInt(this.args.suggestion.entityId, 10),
      entityType: this.args.suggestion.contentType as EntityType,
    });
  }

  get conversationsCountTooltipText() {
    if (this.content?.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.tooltip.unresolved-conversations', {
        contentTypeName: humanReadableObjectNames[this.contentType as EntityType].toLowerCase(),
      });
    }
    return this.intl.t('ai-agent.suggestions.list-item.impact-description', {
      conversation_count: this.conversationsCount,
    });
  }

  get duplicateReason(): string {
    return (
      this.loadedDuplicateReason ??
      this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason-generic',
      )
    );
  }

  get contradictingReason(): string {
    return (
      this.loadedContradictingReason ??
      this.processedReviewReason ??
      this.intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-contradicting-reason')
    );
  }

  get isConDupeContent() {
    return (
      this.content?.contentReviewType === ReviewType.CONTRADICTING_CONTENT ||
      this.content?.contentReviewType === ReviewType.DUPLICATE_CONTENT
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::ListItem': typeof ListItem;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/list-item': typeof ListItem;
  }
}
